import { UNCATEGORISED, ME, type DeckFilterState } from './deckFilterState.ts';
import { type DeckModelOption } from './useDeckFilters.ts';
import type { CategoryColour } from '../../../shared/api.ts';

/**
 * The active-filter chip row, as data.
 *
 * The row has to cover EVERY narrowing axis, not just the ones that have a
 * control today: `model` and `updated` arrive from the URL long before Run 3
 * builds their pills, and a filter with no visible cause and no way to clear it
 * is a dashboard that looks broken. Deriving the chips from the filter state
 * rather than from the controls is what makes that true by construction — add
 * an axis to `FILTER_AXES` and it gets a chip whether or not anything renders a
 * menu for it.
 *
 * Pure, so the labels and the ordering can be asserted without a DOM.
 */

/** The axes a chip can clear. Sort, view and tile size are presentation, not
 *  filters, so they are deliberately absent — see `FILTER_AXES`. */
export type FilterChipAxis = 'status' | 'category' | 'model' | 'owner' | 'updated' | 'query';

export type FilterChip = {
  axis: FilterChipAxis;
  /** The axis name, ending in a colon: "Status:". */
  label: string;
  /** What it is set to, in the user's words. */
  value: string;
  /** The token name behind the chip's dot (`var(--accent)`, `var(--lime)`…),
   *  or null for the default muted dot. Never a colour literal. */
  dot: CategoryColour | 'accent' | null;
  /** What the × button announces it will do. */
  clearLabel: string;
};

type ChipInput = {
  filters: DeckFilterState;
  /** The model roster, for turning a stored id back into its label. */
  models: readonly DeckModelOption[];
  /** A category's registry tint, if it has one — the design's coloured dot. */
  categoryColour?: (path: string) => CategoryColour | null;
  /** Turns an owner id into a name for the chip. Absent, or a person who has
   *  been deleted, falls back to the id — an unlabelled chip is still a chip
   *  you can see and clear, which is the point of this row. */
  personName?: (id: string) => string | null;
};

const STATUS_LABELS: Record<'published' | 'unpublished' | 'trash', string> = {
  published: 'Published',
  unpublished: 'Drafts',
  trash: 'Trash',
};

/** Spans, not calendar buckets, apart from Today — see `updatedWindowStart`,
 *  which these words have to describe accurately. */
const UPDATED_LABELS: Record<'today' | 'week' | 'month' | 'quarter', string> = {
  today: 'Today',
  week: 'Last 7 days',
  month: 'Last 30 days',
  quarter: 'Last 90 days',
};

/**
 * "Showing 12 of 62 decks" — the filtered count, which the title row's
 * standing counts deliberately do not carry.
 */
export function showingLine(shown: number, total: number): string {
  return `Showing ${shown} of ${total} ${total === 1 ? 'deck' : 'decks'}`;
}

/**
 * The same line for the BIN, which is not the grid (Run 11).
 *
 * The bin REPLACES the body rather than narrowing it, and none of the other
 * axes touch it, so `showingLine` printed `Showing 0 of 61 decks` over a panel
 * holding three — a sentence about a list that is not on screen. The bin counts
 * itself instead.
 */
export function binLine(count: number): string {
  return `${count} ${count === 1 ? 'deck' : 'decks'} in the bin`;
}

/**
 * The chips that mean anything in the bin: the status chip alone.
 *
 * The other axes stay in the state and get their chips back the moment you
 * leave, but while the bin is on screen a `Folder: Clients` chip claims to be
 * narrowing a list it does not touch, which is the same bug as the count line
 * in a smaller font. Clear all still clears every axis, and taking the Trash
 * chip off is what puts you back among the decks.
 */
export function binChips(chips: readonly FilterChip[]): FilterChip[] {
  return chips.filter((c) => c.axis === 'status');
}

/**
 * One chip per set filter, in the order the design lists them: what narrowed
 * the list most structurally first, the free-text search last.
 */
export function activeFilterChips({ filters, models, categoryColour, personName }: ChipInput): FilterChip[] {
  const chips: FilterChip[] = [];

  if (filters.status) {
    const value = STATUS_LABELS[filters.status];
    chips.push({
      axis: 'status',
      label: 'Status:',
      value,
      // Status is the one axis with no colour of its own, and it is also the
      // one the page leads with, so it takes the brand accent.
      dot: 'accent',
      clearLabel: `Clear the ${value} status filter`,
    });
  }

  if (filters.category) {
    const uncategorised = filters.category === UNCATEGORISED;
    const value = uncategorised ? 'Uncategorised' : filters.category;
    chips.push({
      axis: 'category',
      label: 'Folder:',
      value,
      // The registry's tint, so the chip's dot matches the rail row it came
      // from. Uncategorised is not in the registry and never has one.
      dot: uncategorised ? null : (categoryColour?.(filters.category) ?? null),
      clearLabel: `Clear the ${value} folder filter`,
    });
  }

  if (filters.model) {
    // A model can be filtered by URL after the last deck built with it was
    // deleted, so the raw id is the fallback rather than an empty chip.
    const value = models.find((m) => m.id === filters.model)?.label ?? filters.model;
    chips.push({
      axis: 'model',
      label: 'Model:',
      value,
      dot: null,
      clearLabel: `Clear the ${value} model filter`,
    });
  }

  if (filters.owner) {
    const value = filters.owner === ME ? 'My decks' : (personName?.(filters.owner) ?? filters.owner);
    chips.push({
      axis: 'owner',
      label: filters.owner === ME ? '' : 'Owner:',
      value,
      dot: null,
      clearLabel: filters.owner === ME ? 'Stop showing only your decks' : `Clear the ${value} owner filter`,
    });
  }

  if (filters.updated) {
    const value = UPDATED_LABELS[filters.updated];
    chips.push({
      axis: 'updated',
      label: 'Updated:',
      value,
      dot: null,
      clearLabel: `Clear the ${value} updated filter`,
    });
  }

  if (filters.query) {
    chips.push({
      axis: 'query',
      label: 'Search:',
      value: filters.query,
      dot: null,
      clearLabel: `Clear the search for ${filters.query}`,
    });
  }

  return chips;
}
